import type { AttentionReason } from "@/components/coach/ActionNeededBanner";
import type { TranscriptItem } from "@/hooks/useCoachSessionPrepData";
import { buildCoachPostMeetingRoute } from "@/lib/coachPostMeetingRoute";
import { isTranscriptNeedsCoachReview } from "@/lib/transcriptStatus";

export type CoachSessionPrepAttentionViewModel = {
  pendingTranscripts: TranscriptItem[];
  latestPendingTranscript: TranscriptItem | null;
  latestReviewedTranscript: TranscriptItem | null;
  attentionReasons: AttentionReason[];
};

export function buildCoachSessionPrepAttention(params: {
  transcripts: TranscriptItem[];
  unreadMessageCount: number;
  leaderId?: string;
  /** When true the transcript IDs are synthetic demo IDs with no DB rows — keep navigation on-page. */
  isDemoLeader?: boolean;
}): CoachSessionPrepAttentionViewModel {
  const pendingTranscripts = params.transcripts.filter((t) =>
    isTranscriptNeedsCoachReview(t.analysis_status, t.coach_verified),
  );
  const latestPendingTranscript = pendingTranscripts[0] || null;
  const latestReviewedTranscript =
    params.transcripts.find((t) => !isTranscriptNeedsCoachReview(t.analysis_status, t.coach_verified)) || null;

  const attentionReasons: AttentionReason[] = [];
  if (pendingTranscripts.length > 0) {
    const primaryPendingTranscript = pendingTranscripts[0];
    // Demo leaders have synthetic transcript IDs not backed by transcript_uploads rows.
    // For them, always scroll to the on-page review section instead of navigating to
    // /coach/post-meeting/:id which would hit a not-found state.
    const singlePendingAction: AttentionReason["action"] = (!params.isDemoLeader && pendingTranscripts.length === 1)
      ? {
          type: "navigate",
          to: buildCoachPostMeetingRoute(primaryPendingTranscript.id, {
            from: "leaders",
            leaderId: params.leaderId,
          }),
        }
      : { type: "scroll", target: "section-transcript-review" };

    attentionReasons.push({
      id: "pending-transcripts",
      label:
        pendingTranscripts.length === 1
          ? `Open transcript review: ${primaryPendingTranscript.title}`
          : `${pendingTranscripts.length} transcripts awaiting review — scroll to review section`,
      icon: "transcript",
      action: pendingTranscripts.length === 1
        ? singlePendingAction
        : { type: "scroll", target: "section-transcript-review" },
    });
  }

  if (params.unreadMessageCount > 0 && params.leaderId) {
    attentionReasons.push({
      id: "unread-messages",
      label: `${params.unreadMessageCount} unread message${params.unreadMessageCount === 1 ? "" : "s"} from leader`,
      icon: "message",
      action: { type: "navigate", to: `/coach/messages?leaderId=${encodeURIComponent(params.leaderId)}` },
    });
  }

  return {
    pendingTranscripts,
    latestPendingTranscript,
    latestReviewedTranscript,
    attentionReasons,
  };
}
